import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { BlueprintFamilyApiItem, useGetBlueprintFamiliesQuery, useGetGenreTaxonomyQuery, useGetTagGroupsListQuery } from '../../../api/core'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { ConventionalCategory } from '../../genre-taxonomy/types/GenreTaxonomy'
import {
  buildTaxonomyCounts,
  buildTaxonomyWindowSearch,
  DetailPageNavState,
  patchCatalogueSearch,
  resolveEffectiveSubgenreIds,
  WINDOW_TO_DAYS,
} from '../components/helpers/catalogueUtils'
import { readDetailPageNavState } from '../components/helpers/detailPageNavState'
import { TagGroupInfo } from '../components/helpers/tagGroups'
import { EventTaxonomy, TaxonomyRelations, useMarketCatalogueState } from './useMarketCatalogueState'

// Parent lookups the URL state uses to prune orphaned genre / subgenre picks.
export function buildTaxonomyRelations(taxonomy: readonly ConventionalCategory[] | undefined): TaxonomyRelations {
  const genreToCategory: Record<string, string> = {}
  const subgenreToCategory: Record<string, string> = {}
  const subgenreToGenre: Record<string, string> = {}
  for (const category of taxonomy ?? []) {
    for (const genre of category.genres) {
      genreToCategory[genre.id] = category.id
      for (const subgenre of genre.subgenres) {
        subgenreToCategory[subgenre.id] = category.id
        subgenreToGenre[subgenre.id] = genre.id
      }
    }
  }
  return { genreToCategory, subgenreToCategory, subgenreToGenre }
}

export interface UseCatalogueScopeOptions {
  // Validate `ecat` / `etype` against the event tag groups once they have loaded with
  // at least one event type, so the params are never dropped against a pending or
  // failed request. Only the catalogue reads those params.
  validateEventFilters?: boolean
}

/**
 * The URL-backed catalogue state and the segment scope every Live Event Analytics
 * page derives from it: the window, the taxonomy picks expanded to subgenres (a
 * category / genre is the union of its subgenres, and deeper picks win per branch),
 * the scope body shared by every scoped endpoint, and the searches the hierarchy
 * links carry. The taxonomy and event tag-group queries share their cache entries
 * with the filter bars.
 */
export function useCatalogueScope({ validateEventFilters = false }: UseCatalogueScopeOptions = {}) {
  const userLanguage = useCurrentUserLanguage()
  const location = useLocation()

  const taxonomyQuery = useGetGenreTaxonomyQuery({ userLanguage })
  const taxonomy = taxonomyQuery.data
  const taxonomyRelations = useMemo(() => buildTaxonomyRelations(taxonomy), [taxonomy])

  const tagGroupsQuery = useGetTagGroupsListQuery({ type: 'event', userLanguage })
  const tagGroups = tagGroupsQuery.data
  const eventTaxonomy = useMemo<EventTaxonomy | undefined>(() => {
    if (!validateEventFilters || !tagGroups?.some((group) => group.tags?.length)) return undefined
    return new Map(tagGroups.map((group) => [group.id, group.tags ?? []]))
  }, [validateEventFilters, tagGroups])
  const tagGroupInfos = useMemo<TagGroupInfo[]>(
    () => (tagGroups ?? []).map((g) => ({ id: g.id, name: g.name, colorHex: g.colorHex, order: g.order })),
    [tagGroups]
  )

  const state = useMarketCatalogueState(taxonomyRelations, eventTaxonomy)

  const windowDays = WINDOW_TO_DAYS[state.window]
  const {
    conventionalCategoryIds: selectedCategoryIds,
    conventionalGenreIds: selectedGenreIds,
    conventionalSubgenreIds: selectedSubgenreIds,
  } = state.categoryGenre
  const effectiveSubgenreIds = useMemo(
    () => resolveEffectiveSubgenreIds(taxonomy, selectedCategoryIds, selectedGenreIds, selectedSubgenreIds),
    [taxonomy, selectedCategoryIds, selectedGenreIds, selectedSubgenreIds]
  )
  const conventionalSubgenreIds = useMemo(() => (effectiveSubgenreIds.length ? effectiveSubgenreIds : undefined), [effectiveSubgenreIds])
  const scope = useMemo(() => ({ conventionalSubgenreIds }), [conventionalSubgenreIds])
  const isUnscoped = conventionalSubgenreIds === undefined
  // A category / genre pick can only be expanded to subgenres once the taxonomy has
  // loaded; until then the scope above is incomplete (or the whole market).
  const scopePending = !taxonomy && (selectedCategoryIds.length > 0 || selectedGenreIds.length > 0)

  // Taxonomy + window only, so links out of a page stay clean and shareable.
  const carriedSearch = useMemo(
    () => buildTaxonomyWindowSearch(selectedCategoryIds, selectedGenreIds, selectedSubgenreIds, state.window),
    [selectedCategoryIds, selectedGenreIds, selectedSubgenreIds, state.window]
  )

  // Arriving from the catalogue carries its full view state, so the root crumb can
  // return the user to the catalogue view they left (segment, window, granularity,
  // view, sort). Direct links, refreshes and new tabs have none and fall back to the
  // reconstruction.
  //
  // The event category / type picks are the hierarchy the user drilled into, which the
  // root crumb sits above, so they are cleared: the root crumb returns to the top of
  // the hierarchy on every detail page, whether or not the catalogue had a pick active.
  const navState = readDetailPageNavState(location.state)
  const rootCrumbSearch = useMemo(
    () =>
      navState?.catalogueBackSearch === undefined ? carriedSearch : patchCatalogueSearch(navState.catalogueBackSearch, { ecat: undefined, etype: undefined }),
    [navState?.catalogueBackSearch, carriedSearch]
  )
  // Passed on by every link to another hierarchy page, so its root crumb still returns
  // to the same catalogue view.
  const crumbNavState = useMemo<DetailPageNavState>(() => ({ catalogueBackSearch: rootCrumbSearch }), [rootCrumbSearch])

  return {
    state,
    taxonomy,
    taxonomyQuery,
    taxonomyRelations,
    tagGroups,
    tagGroupsQuery,
    tagGroupInfos,
    windowDays,
    selectedCategoryIds,
    selectedGenreIds,
    selectedSubgenreIds,
    effectiveSubgenreIds,
    conventionalSubgenreIds,
    scope,
    isUnscoped,
    scopePending,
    carriedSearch,
    navState,
    rootCrumbSearch,
    crumbNavState,
  }
}

/**
 * Taxonomy picker counts from an UNSCOPED catalogue snapshot: the picker hides nodes
 * with no blueprints behind them, and tallying the scoped list would hide every node
 * outside the current segment. Membership doesn't depend on `windowDays`, so the bare
 * `{}` args share one cache entry across the pages, and it is only fetched while a
 * scope is active (unscoped, the page's own list already is the snapshot).
 */
export function useCatalogueTaxonomyCounts(
  catalogueList: BlueprintFamilyApiItem[] | undefined,
  isUnscoped: boolean,
  { skip = false }: { skip?: boolean } = {}
) {
  const { data: allBlueprints } = useGetBlueprintFamiliesQuery({}, { skip: isUnscoped || skip })
  const taxonomyCounts = useMemo(() => buildTaxonomyCounts((isUnscoped ? catalogueList : allBlueprints) ?? []), [isUnscoped, catalogueList, allBlueprints])
  return { allBlueprints, taxonomyCounts }
}
